import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { TApiReadyCovenant, TApiReadyCovenantDocument } from '~/modules/covenants/types.ts';
import { getCovenantsRequestErrorMessage } from '~/modules/covenants/utils.ts';
import {
  fetchSupervisorApplicationReadyCovenantDocuments,
} from '~/store/covenants/thunks/supervisorApplicationThunks.ts';
import type { AppDispatch } from '~/store/store.ts';
import { MANAGER_READY_COVENANTS_DOCUMENTS_BATCH_SIZE } from '../constants.ts';
import {
  getNextDocumentBatchIds,
  getReadyCovenantKey,
  mergeReadyCovenantDocuments,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/utils/utils.ts';

type TUseManagerReadyCovenantDocumentsParams = {
  applicationId: string | null;
  open: boolean;
};

export const useManagerReadyCovenantDocuments = ({
  applicationId,
  open,
}: TUseManagerReadyCovenantDocumentsParams) => {
  const dispatch = useDispatch<AppDispatch>();
  const [visibleDocumentsByCovenantKey, setVisibleDocumentsByCovenantKey] = useState<
    Record<string, TApiReadyCovenantDocument[]>
  >({});
  const [allDocumentsByCovenantKey, setAllDocumentsByCovenantKey] = useState<
    Record<string, TApiReadyCovenantDocument[]>
  >({});
  const [documentsLoadingByCovenantKey, setDocumentsLoadingByCovenantKey] = useState<
    Record<string, boolean>
  >({});
  const [documentsErrorByCovenantKey, setDocumentsErrorByCovenantKey] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    setVisibleDocumentsByCovenantKey({});
    setAllDocumentsByCovenantKey({});
    setDocumentsLoadingByCovenantKey({});
    setDocumentsErrorByCovenantKey({});
  }, [applicationId, open]);

  const handleLoadDocuments = useCallback(
    async (covenant: TApiReadyCovenant) => {
      if (!applicationId) {
        return;
      }

      const covenantKey = getReadyCovenantKey(covenant);
      if (documentsLoadingByCovenantKey[covenantKey]) {
        return;
      }

      const visibleDocuments = visibleDocumentsByCovenantKey[covenantKey] ?? [];
      const allDocuments = allDocumentsByCovenantKey[covenantKey] ?? [];
      const nextVisibleCount = visibleDocuments.length + MANAGER_READY_COVENANTS_DOCUMENTS_BATCH_SIZE;

      if (allDocuments.length > visibleDocuments.length) {
        setVisibleDocumentsByCovenantKey((previousState) => ({
          ...previousState,
          [covenantKey]: allDocuments.slice(0, nextVisibleCount),
        }));
        return;
      }

      const nextDocumentIds = getNextDocumentBatchIds({
        documentIdsGroup: covenant.documentIdsGroup,
        loadedDocuments: allDocuments,
        batchSize: MANAGER_READY_COVENANTS_DOCUMENTS_BATCH_SIZE,
      });

      if (!nextDocumentIds.length) {
        return;
      }

      setDocumentsLoadingByCovenantKey((previousState) => ({
        ...previousState,
        [covenantKey]: true,
      }));
      setDocumentsErrorByCovenantKey((previousState) => ({
        ...previousState,
        [covenantKey]: '',
      }));

      try {
        const response = await dispatch(
          fetchSupervisorApplicationReadyCovenantDocuments({
            applicationId,
            documentIds: nextDocumentIds,
            yearPeriod: covenant.yearPeriod,
            childPeriod: covenant.childPeriod,
            deadline: covenant.deadline,
          })
        ).unwrap();

        const mergedOrderedDocuments = mergeReadyCovenantDocuments({
          currentDocuments: allDocuments,
          fetchedDocuments: response.documents ?? [],
          documentIdsGroup: covenant.documentIdsGroup,
        });

        setAllDocumentsByCovenantKey((previousState) => ({
          ...previousState,
          [covenantKey]: mergedOrderedDocuments,
        }));
        setVisibleDocumentsByCovenantKey((previousState) => ({
          ...previousState,
          [covenantKey]: mergedOrderedDocuments.slice(0, nextVisibleCount),
        }));
      } catch (requestError) {
        setDocumentsErrorByCovenantKey((previousState) => ({
          ...previousState,
          [covenantKey]: getCovenantsRequestErrorMessage(requestError),
        }));
      } finally {
        setDocumentsLoadingByCovenantKey((previousState) => ({
          ...previousState,
          [covenantKey]: false,
        }));
      }
    },
    [
      allDocumentsByCovenantKey,
      applicationId,
      dispatch,
      documentsLoadingByCovenantKey,
      visibleDocumentsByCovenantKey,
    ]
  );

  return {
    documentsByCovenantKey: visibleDocumentsByCovenantKey,
    allDocumentsByCovenantKey,
    documentsLoadingByCovenantKey,
    documentsErrorByCovenantKey,
    handleLoadDocuments,
  };
};
